/**
 * Composer key handling
 *
 * Decides whether an Enter keypress in a message composer should send.
 *
 * Why this is shared
 * ------------------
 * Every chat composer had the same handler:
 *
 *     if (e.key === "Enter" && !e.shiftKey) { send() }
 *
 * which is correct on a desktop keyboard and broken everywhere else:
 *
 * - **Touch keyboards have no Shift key.** Return therefore always sent, and
 *   writing more than one line was impossible. Since these are conversations
 *   about relationships, and people write more than one line about those, this
 *   was a real limitation rather than a nicety.
 *
 * - **IME input.** For Japanese, Chinese, Korean and others, Enter confirms the
 *   selected candidate. Sending on it truncates the word being typed.
 *
 * Sending stays available on every device via the send button, which is the
 * same convention iMessage, WhatsApp and Slack-on-mobile use.
 */

/**
 * Whether the primary pointer is coarse — a finger rather than a mouse.
 *
 * Used instead of a viewport width query: a small laptop window is still a
 * keyboard device, and a large tablet still has no Shift key. Width answers a
 * different question than the one being asked.
 */
export function isCoarsePointer(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    // No matchMedia (jsdom, older browsers) — assume a keyboard, preserving
    // the long-standing desktop behaviour.
    return false;
  }
  return window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Whether this keypress should send the message.
 *
 * @param event - The keydown event from the composer.
 * @returns true to send; false to let the key behave normally.
 */
export function shouldSendOnEnter(
  event: Pick<React.KeyboardEvent, "key" | "shiftKey"> & {
    nativeEvent?: { isComposing?: boolean };
  }
): boolean {
  if (event.key !== "Enter") return false;

  // Explicit newline on a keyboard.
  if (event.shiftKey) return false;

  // Mid-composition: Enter is confirming an IME candidate, not submitting.
  if (event.nativeEvent?.isComposing) return false;

  // No Shift available, so Enter has to mean newline. Send is the button.
  if (isCoarsePointer()) return false;

  return true;
}
